import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, get, isEqual, isFunction, find } from 'lodash';
import { connect } from 'react-redux';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { browserHistory } from 'common/utils/browserHistory';
import {
  hasLimitedGenderOptions,
  cl787SexualityFields,
  scrn7ScreeningsCompletion,
} from 'common/utils/FeatureFlags/flags';
import { Button } from '@unite-us/app-components';
import { isHttpError } from 'common/utils/Error';
import { OverlaySpinner, Spinner } from 'common/spinners';
import findCurrentGroup from 'common/utils/findCurrentGroup';
import { getGroup } from 'common/utils/stateHelpers';
import { getEnumsFromState } from 'common/utils/Enums';
import { getInitialPaymentsFormData } from 'src/components/Insurance/utils/';
import { fetchGroupContact, fetchProfileForms } from 'actions/Contact/Group';
import { fetchGroup } from 'actions/Group';
import { removeSelectedContact } from 'actions/Contact';
import { DOMAIN_CONSTANTS } from 'src/common/constants';
import enforcedConsentRoute from 'common/utils/Navigation/enforcedConsentRoute';
import { buildAccessibilityMessageFromErrors } from 'common/utils/Form';
import AddSupportingContactDetailsStep from 'pages/components/AddSupportingContactDetailsStep';
import { formatNewContactDataFromValues } from '../utils/formatNewContactData';
import Header from './Header';
import BasicInformation from './BasicInformation';
import ContactInformation from './ContactInformation';
import SupplementalInformation from './SupplementalInformation';
import ContactRequirementsForm from './ContactRequirementsForm';

import {
  COPY_CREATE_CLIENT,
  COPY_UPDATE_CLIENT,
  NOT_FOUND_HEADER_COPY,
  REGULAR_HEADER_COPY,
} from '../constants';
import {
  goToSearchStep,
  defaultPhoneNumber,
  defaultEmailAddress,
  defaultAddress,
  getInitialValuesFromContact,
} from '../utils';

const initialPhoneNumber = defaultPhoneNumber(true);
const initialEmail = defaultEmailAddress(true);
const initialAddress = defaultAddress({ is_primary: true });

// Helper functions to reduce cyclomatic complexity
const getContactForInitialValues = (contact, selectedContact) => (
  !isEmpty(contact) ? contact : selectedContact
);

const getHeaderText = (existingContact) => (
  existingContact ? COPY_UPDATE_CLIENT : COPY_CREATE_CLIENT
);

const getSubHeaderText = (existingContact) => (
  existingContact ? REGULAR_HEADER_COPY : `${NOT_FOUND_HEADER_COPY} ${REGULAR_HEADER_COPY}`
);

const getSubmittingText = (existingContact) => (
  existingContact ? 'Updating Client...' : 'Creating Client...'
);

// Helper to check if any dependency has changed
const hasAnyDepChanged = (cachedDeps, currentDeps) => (
  cachedDeps.contactHash !== currentDeps.contactHash ||
  cachedDeps.selectedContactHash !== currentDeps.selectedContactHash ||
  cachedDeps.contactDataId !== currentDeps.contactDataId ||
  cachedDeps.insurancesHash !== currentDeps.insurancesHash
);

export class AddContactInformationForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isFetching: false,
      isSubmitting: false,
      initialPaymentsIds: [],
      profileForms: [],
      showProfileForms: false,
      allLanguages: [],
      userLanguageEdits: {},
      // Accessibility features
      errorCount: 0,
      accessibilityMessage: '',
      accessibilityMessageKey: 0,
      // Track whether form has been submitted (for validation display)
      submitFailed: false,
    };

    // Cache for memoizing initial values
    this.cachedInitialValues = null;
    this.cachedDeps = {
      contactHash: null,
      selectedContactHash: null,
      contactDataId: null,
      insurancesHash: null,
    };

    // RFF form API ref
    this.formRef = null;

    this.onBackClick = this.onBackClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.finalizeStep = this.finalizeStep.bind(this);
    this.getInitialValues = this.getInitialValues.bind(this);
    this.renderProfileForms = this.renderProfileForms.bind(this);
    this.renderContactForm = this.renderContactForm.bind(this);
  }

  /* eslint-disable react/sort-comp */
  checkInitialValuesDepsChanged(contact, selectedContact, contactData) {
    const currentDeps = {
      contactHash: JSON.stringify(contact),
      selectedContactHash: JSON.stringify(selectedContact),
      contactDataId: this.extractId(contactData),
      insurancesHash: JSON.stringify(this.props.insurances),
    };

    return hasAnyDepChanged(this.cachedDeps, currentDeps);
  }

  // eslint-disable-next-line class-methods-use-this
  buildInitialValues(contact, selectedContact, contactData, insurances) {
    const contactForInitialValues = getContactForInitialValues(contact, selectedContact);
    const selectedContactData = getInitialValuesFromContact(contactForInitialValues || {}) || {};
    const decodedContactData = getInitialValuesFromContact(contactData) || {};
    const paymentData = getInitialPaymentsFormData(insurances || []) || {};

    return {
      gender: 'undisclosed',
      marital_status: 'undisclosed',
      race: 'undisclosed',
      ethnicity: 'undisclosed',
      sexuality: [],
      phone_numbers: [initialPhoneNumber],
      email_addresses: [initialEmail],
      addresses: [initialAddress],
      ...decodedContactData,
      ...selectedContactData,
      ...paymentData,
    };
  }

  mergeLanguageFields(initialValues, selectedContactData) {
    // Start with initial values
    let result = { ...initialValues };

    // Merge selected contact languages (don't let undefined overwrite defined)
    result = this.mergeSelectedContactLanguages(result, selectedContactData);

    // Preserve user's in-progress language edits if they exist
    result = this.applyUserLanguageEdits(result);

    return result;
  }

  // eslint-disable-next-line class-methods-use-this
  mergeSelectedContactLanguages(values, selectedContactData) {
    const merged = { ...values };
    if (selectedContactData.preferred_spoken_languages && !merged.preferred_spoken_languages) {
      merged.preferred_spoken_languages = selectedContactData.preferred_spoken_languages;
    }
    if (selectedContactData.preferred_written_languages && !merged.preferred_written_languages) {
      merged.preferred_written_languages = selectedContactData.preferred_written_languages;
    }
    return merged;
  }

  applyUserLanguageEdits(values) {
    const { userLanguageEdits } = this.state;
    if (!userLanguageEdits) {
      return values;
    }

    const updated = { ...values };
    if (userLanguageEdits.preferred_spoken_languages !== undefined) {
      updated.preferred_spoken_languages = userLanguageEdits.preferred_spoken_languages;
    }
    if (userLanguageEdits.preferred_written_languages !== undefined) {
      updated.preferred_written_languages = userLanguageEdits.preferred_written_languages;
    }
    return updated;
  }

  updateCachedDeps(contact, selectedContact, contactData) {
    this.cachedDeps = {
      contactHash: JSON.stringify(contact),
      selectedContactHash: JSON.stringify(selectedContact),
      contactDataId: this.extractId(contactData),
      insurancesHash: JSON.stringify(this.props.insurances),
    };
  }

  // eslint-disable-next-line class-methods-use-this
  extractId(obj) {
    return obj?.id || null;
  }

  // eslint-disable-next-line class-methods-use-this
  extractPreferredLanguages(contact) {
    return contact?.preferred_languages || null;
  }

  getInitialValues() {
    const {
 contact, contactData, selectedContact, insurances,
} = this.props;

    // Memoization: only recalculate if dependencies have changed
    const depsChanged = this.checkInitialValuesDepsChanged(contact, selectedContact, contactData);

    // If dependencies haven't changed and we have cached values, return them
    if (!depsChanged && this.cachedInitialValues) {
      return this.cachedInitialValues;
    }

    // Dependencies changed, recalculate initial values
    const contactForInitialValues = getContactForInitialValues(contact, selectedContact);
    const selectedContactData = getInitialValuesFromContact(contactForInitialValues || {}) || {};
    let initialValues = this.buildInitialValues(contact, selectedContact, contactData, insurances);

    initialValues = this.mergeLanguageFields(initialValues, selectedContactData);

    // Cache the result and dependencies
    this.cachedInitialValues = initialValues;
    this.updateCachedDeps(contact, selectedContact, contactData);

    return initialValues;
  }

  updateLanguageEdits(fieldName, value) {
    if (fieldName === 'preferred_spoken_languages' || fieldName === 'preferred_written_languages') {
      this.setState((prevState) => ({
        userLanguageEdits: {
          ...prevState.userLanguageEdits,
          [fieldName]: value,
        },
      }));
    }
  }

  renderSupplementalSection() {
    const {
      completeGroup,
      contactId,
      currentGroup,
    } = this.props;

    if (!completeGroup) {
      return <Spinner />;
    }

    return (
      <SupplementalInformation
        contactId={contactId}
        currentGroup={currentGroup}
      />
    );
  }

  renderProfileForms() {
    const existingContact = this.props.contactId || this.props.integrationContact;
    const header = getHeaderText(existingContact);

    return (
      <div>
        <AddSupportingContactDetailsStep
          activeStep={this.props.activeStep}
          profileForms={this.state.profileForms}
          contact={this.props.contact}
          groupId={this.props.groupId}
          contactId={this.props.contactId}
          header={header}
          checkForForms={this.props.checkForAttachedForms}
          clientRoute={this.props.isClientRoute}
          context={this.props.context}
        />
      </div>
    );
  }

  renderContactForm(
    allInitialValues,
    showErrorSummary,
    accessibilityMessage,
    form,
    handleSubmit,
    hasValidationErrors,
  ) {
    const {
      completeGroup,
      contactId,
      enums,
      integrationContact,
      limitedGenderOptions,
      shouldShowSexualityFields,
    } = this.props;

    const { isSubmitting } = this.state;
    const existingContact = contactId || integrationContact;
    const header = getHeaderText(existingContact);
    const subHeader = getSubHeaderText(existingContact);
    const submittingText = getSubmittingText(existingContact);

    return (
      <div>
        <Header header={header} subHeader={subHeader} />
        <form onSubmit={(e) => e.preventDefault()}>
          {showErrorSummary && this.renderErrorSummary(accessibilityMessage)}
          <OverlaySpinner text={submittingText} show={isSubmitting} />
          <OverlaySpinner text="Fetching Client..." show={this.state.isFetching} />
          <BasicInformation
            enums={enums}
            limitedGenderOptions={limitedGenderOptions}
            shouldShowSexualityFields={shouldShowSexualityFields}
            isExistingContact={!!contactId}
          />
          <ContactInformation
            form={form}
          />
          <ContactRequirementsForm
            requireContactPreferences={completeGroup ? completeGroup.require_contact_preferences : false}
            onLanguagesLoaded={(languages) => {
              this.setState({ allLanguages: languages });
            }}
          />
          {this.renderSupplementalSection(allInitialValues)}
          <footer className="mt-6 text-right add-contact-information-form__footer">
            <Button
              id="go-back-btn"
              onClick={this.onBackClick}
              label="Go Back"
              style={{ marginRight: '10px' }}
              disabled={isSubmitting}
            />
            <Button
              id="save-client-btn"
              onClick={() => this.onSaveClientClick(form, handleSubmit, hasValidationErrors)}
              label="Save Client"
              primary
              disabled={isSubmitting}
            />
          </footer>
        </form>
      </div>
    );
  }

  componentDidMount() {
    const {
      contactId,
      completeGroup,
      groupId,
      insurances,
      checkForAttachedForms,
    } = this.props;

    const initialIds = insurances
      .filter((insurance) => insurance.id !== '')
      .map((insurance) => ({
        id: insurance.id,
        plan_type: insurance.relationships.plan.data.planType,
      }));

    this.setInitialPaymentsIds(initialIds);

    if (contactId) {
      this.setState({ isFetching: true });
      this.props.fetchGroupContact(groupId, contactId).then(() => {
        this.setState({ isFetching: false });
      });
    }

    if (!completeGroup) {
      this.props.fetchGroup(this.props.groupId);
    }

    if (checkForAttachedForms) {
      this.fetchProfileForms();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    this.handleContactIdChange(prevProps);
    this.handleLanguageChanges(prevProps);
    this.handleInsuranceChanges(prevProps);
    this.handleAccessibilityMessageUpdates(prevState);
  }

  handleContactIdChange(prevProps) {
    const prevContactId = prevProps.contact?.id || prevProps.selectedContact?.id;
    const currentContactId = this.props.contact?.id || this.props.selectedContact?.id;

    const shouldClearLanguageEdits = (
      prevContactId !== currentContactId &&
      currentContactId
    );

    if (shouldClearLanguageEdits) {
      this.setState({ userLanguageEdits: {} });
    }
  }

  handleLanguageChanges(prevProps) {
    const prevContactId = this.getContactId(prevProps);
    const currentContactId = this.getContactId(this.props);
    const prevLanguages = this.extractPreferredLanguages(prevProps.contact);
    const currentLanguages = this.extractPreferredLanguages(this.props.contact);

    const shouldClearLanguageEdits = this.shouldClearLanguageEditsForContact(
      prevContactId,
      currentContactId,
      prevLanguages,
      currentLanguages,
    );

    if (shouldClearLanguageEdits) {
      this.setState({ userLanguageEdits: {} });
    }
  }

  // eslint-disable-next-line class-methods-use-this
  shouldClearLanguageEditsForContact(prevContactId, currentContactId, prevLanguages, currentLanguages) {
    const isSameContact = prevContactId === currentContactId;
    if (!isSameContact) return false;

    const languagesChanged = prevLanguages !== currentLanguages;
    if (!languagesChanged) return false;

    const hasCurrentLanguages = !!currentLanguages;
    return hasCurrentLanguages;
  }

  // eslint-disable-next-line class-methods-use-this
  getContactId(props) {
    return props.contact?.id || props.selectedContact?.id;
  }

  handleInsuranceChanges(prevProps) {
    if (this.props.insurances !== prevProps.insurances) {
      const currentInitialIds = this.props.insurances
        .filter((insurance) => insurance.id !== '')
        .map((insurance) => ({
          id: insurance.id,
          plan_type: insurance.relationships.plan.data.planType,
        }));

      this.setInitialPaymentsIds(currentInitialIds);
    }
  }

  handleSubmitFailedChanged() {
    this.updateAccessibilityMessage(true, 200, 50);
  }

  handleSubmitFailedCleared() {
    this.setState({ errorCount: 0, accessibilityMessage: '', accessibilityMessageKey: 0 });
  }

  handleMessageClearedButErrorsRemain() {
    this.updateAccessibilityMessage(false, 200, 300);
  }

  handleResubmittingWithErrors() {
    this.updateAccessibilityMessage(true, 300, 300);
  }

  handleAccessibilityMessageUpdates(prevState) {
    if (this.checkSubmitFailedChanged(prevState)) {
      this.handleSubmitFailedChanged();
      return;
    }

    if (this.checkSubmitFailedCleared(prevState)) {
      this.handleSubmitFailedCleared();
      return;
    }

    if (this.checkMessageClearedButErrorsRemain(prevState)) {
      this.handleMessageClearedButErrorsRemain();
      return;
    }

    if (this.checkResubmittingWithErrors(prevState)) {
      this.handleResubmittingWithErrors();
    }
  }

  onBackClick() {
    const { context, activeStep } = this.props;

    if (isFunction(activeStep.onBackClick)) {
      return activeStep.onBackClick();
    }
    return goToSearchStep({ context });
  }

  validateLanguagesLoaded() {
    return this.state.allLanguages && this.state.allLanguages.length > 0;
  }

  onSaveClientClick = (form, handleSubmit, hasValidationErrors) => {
    this.setState({
      accessibilityMessage: '',
      accessibilityMessageKey: this.state.accessibilityMessageKey + 1,
    });

    if (hasValidationErrors) {
      // Touch all fields to trigger validation error display
      const registeredFields = form.getRegisteredFields();
      registeredFields.forEach((fieldName) => {
        form.blur(fieldName);
        form.focus(fieldName);
        form.blur(fieldName);
      });
      this.setState({ submitFailed: true });
      return;
    }

    handleSubmit();
  }

  handlePostSubmitNavigation(payload) {
    if (!payload) return;

    const { checkForAttachedForms, contactId, context } = this.props;
    const { profileForms } = this.state;

    const shouldShowProfileForms = checkForAttachedForms && profileForms.length;
    const isReferralWithContact = context.includes(DOMAIN_CONSTANTS.REFERRAL) && contactId != null;

    if (shouldShowProfileForms) {
      this.setState({ showProfileForms: true });
    } else if (isReferralWithContact) {
      browserHistory.push({
        pathname: '/referrals/create/add-resources',
        search: `?person=${contactId}`,
        state: { source: 'client' },
      });
    } else if (!checkForAttachedForms || !profileForms.length) {
      this.finalizeStep(payload);
    } else {
      this.setState({ showProfileForms: true });
    }
  }

  onSubmit(values = {}) {
    const { createOrUpdateContact } = this.props;

    this.setState({ isSubmitting: true, submitFailed: false });

    if (!this.validateLanguagesLoaded()) {
      this.setState({ isSubmitting: false });
      return Promise.resolve(null);
    }

    const contactData = formatNewContactDataFromValues(values);
    const insuranceValues = values.insurance || [];

    return createOrUpdateContact(
      contactData,
      insuranceValues,
      this.state.initialPaymentsIds,
      this.state.allLanguages,
    ).then((payload) => {
      this.setState({ isSubmitting: false }, () => {
        this.handlePostSubmitNavigation(payload);
      });
    });
  }

  setInitialPaymentsIds(initialIds) {
    this.setState({
      initialPaymentsIds: initialIds,
    });
  }

  updateAccessibilityMessage = (clearFirst = false, initialDelay = 200, buildDelay = 50) => {
    buildAccessibilityMessageFromErrors({
      getErrorCount: () => this.state.errorCount,
      setState: (updates, callback) => this.setState(updates, callback),
      clearFirst,
      initialDelay,
      buildDelay,
    });
  }

  checkSubmitFailedChanged(prevState) {
    return !prevState.submitFailed && this.state.submitFailed;
  }

  checkSubmitFailedCleared(prevState) {
    return prevState.submitFailed && !this.state.submitFailed;
  }

  checkMessageClearedButErrorsRemain(prevState) {
    return (
      this.state.submitFailed &&
      !this.state.accessibilityMessage &&
      prevState.accessibilityMessage
    );
  }

  checkResubmittingWithErrors(prevState) {
    return (
      !prevState.isSubmitting &&
      this.state.isSubmitting &&
      this.state.submitFailed
    );
  }

  async fetchProfileForms() {
    const { data: { data } } = await this.props.fetchProfileForms(this.props.groupId);

    if (!data.length) return;

    this.setState({
      profileForms: data,
    });
  }

  finalizeStep(payload) {
    if (!isHttpError(payload)) {
      const contact = get(payload, 'data.data', {});
      const {
        activeStep,
        contactId,
        isClientRoute,
        context,
        scrn7ScreeningsCompletionEnabled,
      } = this.props;

      if (
        scrn7ScreeningsCompletionEnabled &&
        context.includes(DOMAIN_CONSTANTS.SCREENING)
      ) {
        const screeningPath = `/facesheet/${contact?.id}/screenings`;
        enforcedConsentRoute({
          contact,
          itemType: DOMAIN_CONSTANTS.SCREENING,
          to: screeningPath,
        });
      } else if (!isClientRoute && activeStep.onStepComplete) {
        activeStep.onStepComplete({
          contact,
          contactId,
          eventTracker: this.context.eventTracker,
          removeSelectedContact: this.props.removeSelectedContact,
        });
      } else {
        enforcedConsentRoute({
          contact,
          itemType: DOMAIN_CONSTANTS.CONTACT,
          to: `/facesheet/${contact.id}`,
        });
      }
    }
  }

  renderErrorSummary(accessibilityMessage) {
    return (
      <div
        key={this.state.accessibilityMessageKey}
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {accessibilityMessage}
      </div>
    );
  }

  render() {
    const { accessibilityMessage, submitFailed } = this.state;

    const allInitialValues = this.getInitialValues();

    const errorCount = this.state.errorCount;
    const showErrorSummary = submitFailed && errorCount > 0;

    return (
      <div className="add-contact-information-form">
        {this.state.showProfileForms ?
          this.renderProfileForms() : (
            <Form
              onSubmit={this.onSubmit}
              initialValues={allInitialValues}
              initialValuesEqual={isEqual}
              mutators={{ ...arrayMutators }}
              validate={(values) => {
                const errors = {};
                const requireContactPrefs = this.props.completeGroup ?
                  this.props.completeGroup.require_contact_preferences :
                  false;
                if (requireContactPrefs) {
                  const timeslots = get(values, 'contact_preferences.timeslots', {});
                  const hasAnyTimeslot = Object.values(timeslots).some(
                    (daySlots) => Array.isArray(daySlots) && daySlots.some(
                      (slot) => slot && slot.start_time && slot.end_time,
                    ),
                  );
                  if (!hasAnyTimeslot) {
                    errors.contact_preferences = {
                      ...errors.contact_preferences,
                      timeslots_required: 'At least one contact time is required',
                    };
                  }
                }
                return errors;
              }}
            >
              {({ form, handleSubmit, hasValidationErrors }) => {
                this.formRef = form;
                return this.renderContactForm(
                  allInitialValues,
                  showErrorSummary,
                  accessibilityMessage,
                  form,
                  handleSubmit,
                  hasValidationErrors,
                );
              }}
            </Form>
          )}
      </div>
    );
  }
  /* eslint-enable react/sort-comp */
}

AddContactInformationForm.propTypes = {
  activeStep: PropTypes.object.isRequired,
  completeGroup: PropTypes.object,
  contact: PropTypes.object.isRequired,
  contactData: PropTypes.object,
  contactId: PropTypes.string,
  context: PropTypes.string,
  createOrUpdateContact: PropTypes.func.isRequired,
  currentGroup: PropTypes.object.isRequired,
  enums: PropTypes.object.isRequired,
  fetchGroup: PropTypes.func.isRequired,
  fetchGroupContact: PropTypes.func.isRequired,
  groupId: PropTypes.string.isRequired,
  insurances: PropTypes.array,
  integrationContact: PropTypes.bool,
  removeSelectedContact: PropTypes.func.isRequired,
  selectedContact: PropTypes.object,
  fetchProfileForms: PropTypes.func.isRequired,
  checkForAttachedForms: PropTypes.bool.isRequired,
  isClientRoute: PropTypes.bool.isRequired,
  scrn7ScreeningsCompletionEnabled: PropTypes.bool,
  limitedGenderOptions: PropTypes.bool.isRequired,
  shouldShowSexualityFields: PropTypes.bool.isRequired,
};

AddContactInformationForm.defaultProps = {
  completeGroup: null,
  contactData: {},
  contactId: null,
  context: '',
  insurances: [],
  integrationContact: false,
  selectedContact: {},
  scrn7ScreeningsCompletionEnabled: false,
};

AddContactInformationForm.contextTypes = {
  eventTracker: PropTypes.func.isRequired,
};

function mapStateToProps(state, ownProps) {
  const {
    user,
    session,
    session: { groupId },
  } = state;

  const {
    contactId,
  } = ownProps;

  const contacts = get(state, 'contacts.contacts', []);
  const searchedContacts = get(state, 'searchedContacts', []);
  const contact =
    find([...contacts, ...searchedContacts], { id: contactId }) || {};

  return {
    completeGroup: getGroup(state, groupId),
    contact,
    contactId,
    currentGroup: findCurrentGroup(user, session),
    enums: getEnumsFromState(state),
    groupId,
    scrn7ScreeningsCompletionEnabled: scrn7ScreeningsCompletion(state),
    limitedGenderOptions: hasLimitedGenderOptions(state),
    shouldShowSexualityFields: cl787SexualityFields(state),
  };
}

export default connect(
  mapStateToProps,
  {
    fetchGroupContact,
    fetchGroup,
    removeSelectedContact,
    fetchProfileForms,
  },
)(AddContactInformationForm);
